<template>
    <div class="prize-pool-mobile">
        <!-- Header -->
        <div class="mobile-header">
            <h3 class="title">{{ $t('prizePool') || 'Prize Pool' }}</h3>
            <div class="participant-count">
                <i class="fa-regular fa-users"></i>
                <span>{{ participantCount.toLocaleString() }}</span>
            </div>
        </div>

        <!-- Current Amount Display -->
        <div class="amount-display">
            <div class="current-amount">${{ prizePool.computedCurrentAmount.toLocaleString() }}</div>
            <div class="base-amount">{{ $t('basePrizePool') || 'Base' }}: ${{ prizePool.baseAmount.toLocaleString() }}</div>
        </div>

        <!-- Progress Bar -->
        <div class="progress-section" v-if="prizePool.nextIncrementIn > 0">
            <div class="progress-info">
                <span class="progress-text">{{ prizePool.nextIncrementIn }} {{ $t('participantsToNext') || 'to next increment' }}</span>
                <span class="increment-amount">+${{ prizePool.incrementAmount.toLocaleString() }}</span>
            </div>
            <div class="progress-bar-container">
                <div class="progress-bar-bg">
                    <div class="progress-bar-fill" :style="progressBarStyle"></div>
                </div>
            </div>
            <div class="next-goal-info">
                <span class="next-goal-label">{{ $t('nextIncrement') || 'Next Goal' }}:</span>
                <span class="next-goal-amount">${{ (prizePool.computedCurrentAmount + prizePool.incrementAmount).toLocaleString() }}</span>
            </div>
        </div>

        <!-- Invite Button -->
        <button class="mobile-invite-btn" @click="$emit('invite')" :style="organizerColorStyle">
            <i class="fa-regular fa-user-plus"></i>
            <span>{{ $t('inviteParticipants') || 'Invite participants' }}</span>
        </button>
    </div>
</template>

<script>
export default {
    name: 'TournamentPrizePoolMobile',
    props: {
        prizePool: {
            type: Object,
            required: true
        },
        participantCount: {
            type: Number,
            required: true
        }
    },
    computed: {
        progressPercentage() {
            if (this.prizePool.incrementEveryNRegs === 0) return 0;
            const currentProgress = this.prizePool.incrementEveryNRegs - this.prizePool.nextIncrementIn;
            return (currentProgress / this.prizePool.incrementEveryNRegs) * 100;
        },
        progressBarStyle() {
            return {
                width: `${this.progressPercentage}%`,
                backgroundColor: this.organizerColor
            };
        },
        organizerColor() {
            if (this.$store.state.tournament?.tournamentSettings?.colors?.primary) {
                const color = this.$store.state.tournament.tournamentSettings.colors.primary;
                return color.startsWith('#') ? color : `#${color}`;
            }
            return '#009F63'; // Default green
        },
        organizerColorStyle() {
            return {
                backgroundColor: this.organizerColor,
                borderColor: this.organizerColor
            };
        }
    }
};
</script>

<style scoped lang="scss">
.prize-pool-mobile {
    background-color: var(--v-fullWhite-base);
    border-radius: 16px;
    padding: 20px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    font-family: "Avenir", sans-serif;
}

.theme--dark .prize-pool-mobile {
    background-color: var(--v-cardBackground-base);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
}

.mobile-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;

    .title {
        font-size: 20px;
        font-weight: 700;
        color: var(--v-mainBlack-base);
        margin: 0;
    }

    .participant-count {
        display: flex;
        align-items: center;
        gap: 6px;
        padding: 6px 12px;
        background-color: var(--v-progressBarBackground-base);
        border-radius: 16px;
        font-size: 13px;
        font-weight: 600;
        color: var(--v-mainBlack-base);

        i {
            font-size: 14px;
        }
    }
}

.amount-display {
    text-align: center;
    margin-bottom: 24px;
    padding: 20px;
    background-color: var(--v-progressBarBackground-base);
    border-radius: 12px;

    .current-amount {
        font-size: 36px;
        font-weight: 700;
        color: var(--v-mainBlack-base);
        margin-bottom: 8px;
    }

    .base-amount {
        font-size: 14px;
        color: var(--v-mainBlack-base);
        opacity: 0.7;
    }
}

.progress-section {
    margin-bottom: 20px;

    .progress-info {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 8px;

        .progress-text {
            font-size: 13px;
            color: var(--v-mainBlack-base);
            opacity: 0.8;
        }

        .increment-amount {
            font-size: 14px;
            font-weight: 700;
            color: #2196F3;
        }
    }

    .progress-bar-container {
        margin-bottom: 12px;

        .progress-bar-bg {
            width: 100%;
            height: 8px;
            background-color: #e0e0e0;
            border-radius: 4px;
            overflow: hidden;

            .progress-bar-fill {
                height: 100%;
                border-radius: 4px;
                transition: width 0.5s ease;
            }
        }
    }

    .theme--dark .progress-bar-bg {
        background-color: #555;
    }

    .next-goal-info {
        display: flex;
        justify-content: space-between;
        align-items: center;

        .next-goal-label {
            font-size: 13px;
            color: var(--v-mainBlack-base);
            opacity: 0.7;
        }

        .next-goal-amount {
            font-size: 16px;
            font-weight: 700;
            color: var(--v-mainBlack-base);
        }
    }
}

.mobile-invite-btn {
    width: 100%;
    padding: 14px 20px;
    background-color: #009F63;
    color: white;
    border: none;
    border-radius: 8px;
    font-size: 15px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    font-family: "Avenir", sans-serif;

    &:active {
        opacity: 0.8;
        transform: scale(0.98);
    }

    i {
        font-size: 16px;
    }
}
</style>
